import { useMutation, useQueryClient } from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { toastErrorApi, toastExito } from "@/lib/toast";
import {
  cambiarEstadoIndicadorImpuesto,
  crearIndicadorImpuesto,
  editarIndicadorImpuesto,
  type CrearIndicadorImpuestoInput,
  type EditarIndicadorImpuestoInput,
} from "../services/indicadores-impuesto-service";

/**
 * Además de `qk.adminIndicadoresImpuesto` (esta pantalla), toda mutación
 * invalida `qk.indicadoresImpuesto` · el catálogo de SOLO activos que
 * consumen los selectores de captura del prorrateo (Etapa 7) · así un
 * alta/edición/baja se refleja de inmediato ahí también.
 */
function invalidarCatalogos(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: qk.adminIndicadoresImpuesto });
  void qc.invalidateQueries({ queryKey: qk.indicadoresImpuesto });
}

export function useCrearIndicadorImpuesto(opts?: { onCreado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CrearIndicadorImpuestoInput) => crearIndicadorImpuesto(input),
    onSuccess: (i) => {
      toastExito({ title: "Indicador creado", message: `"${i.codigo}" disponible en el catálogo.` });
      invalidarCatalogos(qc);
      opts?.onCreado?.();
    },
    onError: toastErrorApi,
  });
}

export function useEditarIndicadorImpuesto(opts?: { onEditado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: EditarIndicadorImpuestoInput }) => editarIndicadorImpuesto(id, input),
    onSuccess: () => {
      toastExito({ title: "Indicador actualizado", message: "Cambios guardados." });
      invalidarCatalogos(qc);
      opts?.onEditado?.();
    },
    onError: toastErrorApi,
  });
}

export function useCambiarEstadoIndicadorImpuesto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, activo }: { id: string; codigo: string; activo: boolean }) => cambiarEstadoIndicadorImpuesto(id, activo),
    onSuccess: (_data, vars) => {
      toastExito({
        title: vars.activo ? `"${vars.codigo}" reactivado` : `"${vars.codigo}" dado de baja`,
        message: vars.activo ? "Disponible de nuevo para capturar." : "Deja de ofrecerse en la captura · los renglones históricos conservan su indicador.",
      });
      invalidarCatalogos(qc);
    },
    onError: toastErrorApi,
  });
}
